/**
 * Sidebar → Competitors landing.
 *
 * Two related jobs live here — a mode switcher at the top makes clear which
 * one you're doing:
 *   1. Tracking (A5 v1): watch named competitor domains over time (weekly
 *      digest, rank gaps, backlinks).
 *   2. SERP / content gaps (A5 v2): pick one keyword — we tear down the live
 *      top-N Google results and produce gaps + a recommended outline.
 *
 * This is NOT Keywords research (volume/difficulty/clusters). Keywords picks
 * what to chase; this mode studies who already ranks for that one term.
 *
 * The project selector is shared, so switching modes preserves the project
 * context. Mode + project live in the URL; localStorage seeds defaults only.
 */
import { useEffect, useRef } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import {
  AlertOctagon,
  Globe,
  Search,
  Sparkles,
  Trash2,
  Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import type { Project } from '@seo/types';
import { Page, PageHeader } from '@/components/ui/page';
import { Surface } from '@/components/ui/surface';
import {
  Section,
  SectionHeader,
  SectionTitle,
  SectionDescription,
} from '@/components/ui/section';
import { EmptyState } from '@/components/ui/empty';
import { FormField } from '@/components/ui/form-field';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ErrorBanner } from '@/components/error-banner';
import { Spinner } from '@/components/spinner';
import { StatusPill, humanStatus } from '@/components/status-pill';
import { StatTile } from '@/components/ui/stat-tile';
import { CompetitorCreateButton } from '@/features/competitors/components/competitor-create-button';
import { CompetitorRunButton } from '@/features/competitors/components/competitor-run-button';
import { CompetitorAnalysisCreateButton } from '@/features/competitor-analyses/components/competitor-analysis-create-button';
import { useProjects } from '@/features/projects/use-projects';
import { useCompetitors } from '@/features/competitors/use-competitors';
import { useCompetitorRuns } from '@/features/competitors/use-competitor-runs';
import { useCompetitorAnalyses } from '@/features/competitor-analyses/use-competitor-analyses';
import { apiDelete, summarizeApiError } from '@/lib/api';
import { relTime, hostname } from '@/lib/format';
import { countByStatus, isFailed, isInProgress, isSuccess } from '@/lib/status';
import { cn } from '@/lib/cn';

const PROJECT_FILTER_STORAGE = 'seo:competitor-project';
const MODE_STORAGE = 'seo:competitor-mode';
type Mode = 'tracking' | 'research';

export function CompetitorsRoute(): JSX.Element {
  const projects = useProjects();
  const availableProjects = projects.data?.items ?? [];
  const navigate = useNavigate();

  // Mode + project live in the URL (?mode=research&project=<id>) — the two
  // surfaces sharing this route are addressable, shareable, and Back undoes
  // a mode switch. localStorage only seeds first-visit defaults.
  const search = useSearch({ strict: false }) as {
    mode?: Mode;
    project?: string;
  };
  const mode: Mode = search.mode ?? 'tracking';
  const projectId = search.project ?? '';

  const setMode = (m: Mode): void => {
    try {
      window.localStorage.setItem(MODE_STORAGE, m);
    } catch {
      /* ignore */
    }
    void navigate({
      to: '/competitors',
      search: {
        ...(m === 'research' ? { mode: m } : {}),
        ...(projectId ? { project: projectId } : {}),
      },
    });
  };

  const setProjectId = (v: string): void => {
    try {
      if (v) window.localStorage.setItem(PROJECT_FILTER_STORAGE, v);
      else window.localStorage.removeItem(PROJECT_FILTER_STORAGE);
    } catch {
      /* ignore */
    }
    void navigate({
      to: '/competitors',
      search: {
        ...(mode === 'research' ? { mode } : {}),
        ...(v ? { project: v } : {}),
      },
      replace: true,
    });
  };

  // First-visit defaults, once per mount — never re-applied on Back.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (projects.isLoading) return;
    seededRef.current = true;
    let storedMode: string | null = null;
    let storedProject: string | null = null;
    try {
      storedMode = window.localStorage.getItem(MODE_STORAGE);
      storedProject = window.localStorage.getItem(PROJECT_FILTER_STORAGE);
    } catch {
      /* ignore */
    }
    const seedMode: Mode | undefined =
      !search.mode && storedMode === 'research' ? 'research' : undefined;
    const seedProject =
      !search.project &&
      ((storedProject && availableProjects.some((p) => p.id === storedProject)
        ? storedProject
        : null) ??
        (availableProjects.length === 1 ? availableProjects[0]!.id : null));
    if (seedMode || seedProject) {
      void navigate({
        to: '/competitors',
        search: {
          ...((seedMode ?? mode) === 'research' ? { mode: 'research' as Mode } : {}),
          ...(seedProject
            ? { project: seedProject }
            : projectId
              ? { project: projectId }
              : {}),
        },
        replace: true,
      });
    }
  }, [
    projects.isLoading,
    availableProjects,
    search.mode,
    search.project,
    mode,
    projectId,
    navigate,
  ]);

  const activeProject = availableProjects.find((p) => p.id === projectId);

  return (
    <Page>
      <PageHeader
        title="Competitors"
        description="Watch rival sites over time, or tear down who already ranks for one keyword you want to win."
      />

      <ModeSwitcher mode={mode} onChange={setMode} />

      <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
        <Section>
          <FormField label="Project">
            {availableProjects.length === 0 ? (
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>You don't have any projects yet.</p>
                <Button asChild size="sm" variant="outline">
                  <Link to="/projects">Create one</Link>
                </Button>
              </div>
            ) : (
              <Select value={projectId} onValueChange={(v) => setProjectId(v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick a project" />
                </SelectTrigger>
                <SelectContent>
                  {availableProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>
          {activeProject ? (
            <div className="mt-2 text-xs text-muted-foreground">
              {hostname(activeProject.domain)}
            </div>
          ) : null}

          <ModeQuickSummary mode={mode} projectId={projectId} />
        </Section>

        <div className="space-y-4">
          {!projectId ? (
            <Section>
              <EmptyState
                icon={mode === 'tracking' ? Globe : Search}
                title="Choose a project first"
                description={
                  mode === 'tracking'
                    ? "Pick a project on the left to manage the competitors you're watching."
                    : "Pick a project on the left to analyze who ranks for a keyword."
                }
                variant="inline"
              />
            </Section>
          ) : mode === 'tracking' ? (
            <TrackingBody
              projectId={projectId}
              project={activeProject ?? null}
              allProjects={availableProjects}
              onSwitchMode={setMode}
            />
          ) : (
            <ResearchBody
              projectId={projectId}
              project={activeProject ?? null}
              onSwitchMode={setMode}
            />
          )}
        </div>
      </div>
    </Page>
  );
}

/* ────────────────────────── Mode switcher ────────────────────────── */

function ModeSwitcher({
  mode,
  onChange,
}: {
  mode: Mode;
  onChange: (m: Mode) => void;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-border/70 bg-card">
      <div className="grid gap-0 md:grid-cols-2">
        <ModeCard
          active={mode === 'tracking'}
          onClick={() => onChange('tracking')}
          icon={Globe}
          title="Watch specific competitors"
          question="Did my competitor just publish something?"
          points={[
            'Add the domains you want to keep an eye on',
            'We check every week for new pages, ranking moves, and backlinks',
            'You get a weekly digest of what changed',
          ]}
          useCase="Keeping tabs on rivals you already know"
        />
        <ModeCard
          active={mode === 'research'}
          onClick={() => onChange('research')}
          icon={Sparkles}
          title="Who ranks for this keyword"
          question="How do I outrank the top 10 for this search?"
          points={[
            'Enter one keyword you already want to target',
            'We study the live top results — not invent a keyword list',
            'You get content gaps, scorecards, and a recommended outline',
          ]}
          useCase="After Keywords research — when you've picked the term to win"
        />
      </div>
    </div>
  );
}

function ModeCard({
  active,
  onClick,
  icon: Icon,
  title,
  question,
  points,
  useCase,
}: {
  active: boolean;
  onClick: () => void;
  icon: LucideIcon;
  title: string;
  question: string;
  points: string[];
  useCase: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group flex flex-col gap-3 border-border/60 p-4 text-left transition-colors md:border-r md:last:border-r-0',
        active
          ? 'bg-primary/5 text-foreground'
          : 'hover:bg-accent/30 text-muted-foreground',
      )}
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'grid h-9 w-9 place-items-center rounded-full',
            active ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground',
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <div
            className={cn(
              'text-sm font-semibold',
              active ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {title}
          </div>
          <div className="text-xs italic text-muted-foreground">"{question}"</div>
        </div>
        {active ? (
          <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
            Active
          </span>
        ) : null}
      </div>
      <ul className="space-y-1 pl-1 text-xs text-muted-foreground">
        {points.map((p) => (
          <li key={p} className="flex items-start gap-1.5">
            <span className="mt-0.5 text-primary/60">·</span>
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <div className="mt-auto border-t border-border/50 pt-2 text-[11px] text-muted-foreground">
        Best when: <span className="text-foreground">{useCase}</span>
      </div>
    </button>
  );
}

function ModeQuickSummary({
  mode,
  projectId,
}: {
  mode: Mode;
  projectId: string;
}): JSX.Element | null {
  const tracking = useCompetitors(mode === 'tracking' ? projectId || undefined : undefined);
  const trackingRuns = useCompetitorRuns(mode === 'tracking' ? projectId || undefined : undefined);
  const analyses = useCompetitorAnalyses(mode === 'research' ? projectId || undefined : undefined);

  if (!projectId) return null;

  if (mode === 'tracking') {
    const t = tracking.data?.items ?? [];
    const r = trackingRuns.data?.items ?? [];
    return (
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Surface tone="muted" className="p-3 text-center">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Watching</div>
          <div className="text-xl font-semibold tabular">{t.length}</div>
        </Surface>
        <Surface tone="muted" className="p-3 text-center">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Scans run</div>
          <div className="text-xl font-semibold tabular">{r.length}</div>
        </Surface>
      </div>
    );
  }
  const rows = analyses.data?.items ?? [];
  const counts = countByStatus(rows);
  return (
    <div className="mt-4 grid grid-cols-2 gap-2">
      <Surface tone="muted" className="p-3 text-center">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</div>
        <div className="text-xl font-semibold tabular">{rows.length}</div>
      </Surface>
      <Surface tone="muted" className="p-3 text-center">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Running now</div>
        <div className="text-xl font-semibold tabular">{counts.inProgress}</div>
      </Surface>
    </div>
  );
}

/* ────────────────────────── Tracking body ────────────────────────── */

function TrackingBody({
  projectId,
  project,
  allProjects,
  onSwitchMode,
}: {
  projectId: string;
  project: Project | null;
  allProjects: Project[];
  onSwitchMode: (m: Mode) => void;
}): JSX.Element {
  const competitors = useCompetitors(projectId);
  const runs = useCompetitorRuns(projectId);
  const qc = useQueryClient();

  const items = competitors.data?.items ?? [];
  const runRows = runs.data?.items ?? [];
  const counts = countByStatus(runRows);
  const failedRecent = runRows.filter(
    (r) => isFailed(r.status) && Date.now() - new Date(r.created_at).getTime() < 7 * 86400_000,
  ).length;

  const handleDelete = async (id: string, domain: string): Promise<void> => {
    try {
      await apiDelete(`/v1/competitors/${id}`);
      void qc.invalidateQueries({ queryKey: ['competitors'] });
      toast.success('Stopped tracking this competitor', { description: domain });
    } catch (e) {
      const summary = summarizeApiError(e, { fallbackTitle: "We couldn't remove that competitor. Try again in a moment." });
      toast.error(summary.title, { description: summary.description });
    }
  };

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Being watched" value={items.length} icon={Globe} />
        <StatTile label="Scans run" value={counts.total} icon={Zap} />
        <StatTile
          label="Running now"
          value={counts.inProgress}
          icon={Zap}
          tone={counts.inProgress > 0 ? 'info' : 'muted'}
        />
        <StatTile
          label="Failed this week"
          value={failedRecent}
          icon={AlertOctagon}
          tone={failedRecent > 0 ? 'danger' : 'muted'}
        />
      </div>

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" />
              Who you're watching
            </SectionTitle>
            <SectionDescription>
              The competitor sites we check for new pages and ranking changes every week.
            </SectionDescription>
          </div>
          <div className="flex gap-2">
            <CompetitorRunButton
              projectId={projectId}
              competitors={items}
              buttonProps={{ variant: 'outline', size: 'sm' }}
            />
            <CompetitorCreateButton
              projects={allProjects}
              defaultProjectId={projectId}
              buttonProps={{ size: 'sm' }}
            />
          </div>
        </SectionHeader>

        {competitors.error ? (
          <ErrorBanner error={competitors.error} />
        ) : competitors.isLoading ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Globe}
            title="No competitors tracked"
            description="Add a domain to start watching what they publish and how they rank."
            variant="inline"
            action={
              <CompetitorCreateButton
                projects={allProjects}
                defaultProjectId={projectId}
                label="Add your first competitor"
                buttonProps={{ size: 'sm' }}
              />
            }
          />
        ) : (
          <ul className="space-y-2">
            {items.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between rounded-md border border-border/70 bg-card p-3"
              >
                <div className="min-w-0">
                  <div className="font-mono text-sm">{c.domain}</div>
                  {c.label ? (
                    <div className="text-xs text-muted-foreground">{c.label}</div>
                  ) : null}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Stop tracking ${c.domain}`}
                  onClick={() => handleDelete(c.id, c.domain)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-muted-foreground" />
              Recent scans
            </SectionTitle>
            <SectionDescription>
              Each scan compares what your competitors publish now against last time — you get a digest of what's new.
            </SectionDescription>
          </div>
        </SectionHeader>

        {runs.isLoading ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : runRows.length === 0 ? (
          <EmptyState
            icon={Zap}
            title="No scans yet"
            description={
              items.length === 0
                ? "Add a competitor above, then run a scan to see what they've been up to."
                : 'Run a scan to check what your competitors have published.'
            }
            variant="inline"
            action={
              items.length > 0 ? (
                <CompetitorRunButton
                  projectId={projectId}
                  competitors={items}
                  buttonProps={{ size: 'sm' }}
                />
              ) : undefined
            }
          />
        ) : (
          <ul className="space-y-2">
            {runRows.map((r) => (
              <li key={r.id}>
                <Link
                  to="/competitors/$runId"
                  params={{ runId: r.id }}
                  className="group flex items-center justify-between gap-3 rounded-md border border-border/70 bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent/30"
                >
                  <div>
                    <div className="font-mono text-sm group-hover:text-primary">
                      run-{r.id.slice(0, 8)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {r.competitor_ids.length} competitor
                      {r.competitor_ids.length === 1 ? '' : 's'} ·{' '}
                      {relTime(r.created_at)}
                    </div>
                  </div>
                  <StatusPill status={r.status} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {project ? (
        <div className="rounded-md border border-dashed border-border/60 bg-muted/20 p-3 text-xs text-muted-foreground">
          Tracking keeps an eye on <span className="font-mono text-foreground">{project.domain}</span>'s
          rivals over time. Already know the keyword to beat?{' '}
          <button
            type="button"
            onClick={() => onSwitchMode('research')}
            className="font-medium text-foreground underline underline-offset-2 hover:text-primary"
          >
            Switch to “Who ranks for this keyword”
          </button>
          .
        </div>
      ) : null}
    </>
  );
}

/* ────────────────────────── Research body ────────────────────────── */

function ResearchBody({
  projectId,
  project,
  onSwitchMode,
}: {
  projectId: string;
  project: Project | null;
  onSwitchMode: (m: Mode) => void;
}): JSX.Element {
  const analyses = useCompetitorAnalyses(projectId);
  const rows = analyses.data?.items ?? [];
  const counts = countByStatus(rows);

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Total analyses" value={rows.length} icon={Search} />
        <StatTile
          label="Running now"
          value={counts.inProgress}
          icon={Zap}
          tone={counts.inProgress > 0 ? 'info' : 'muted'}
        />
        <StatTile
          label="Ready to use"
          value={counts.succeeded}
          icon={Sparkles}
          tone={counts.succeeded > 0 ? 'success' : 'muted'}
        />
        <StatTile
          label="Failed"
          value={counts.failed}
          icon={AlertOctagon}
          tone={counts.failed > 0 ? 'danger' : 'muted'}
        />
      </div>

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-muted-foreground" />
              SERP teardowns
            </SectionTitle>
            <SectionDescription>
              One keyword at a time. This studies who already ranks — not the same as Keywords research (finding which terms to chase).
            </SectionDescription>
          </div>
          <CompetitorAnalysisCreateButton
            projects={project ? [project] : []}
            defaultProjectId={projectId}
            buttonProps={{ size: 'sm' }}
          />
        </SectionHeader>

        {analyses.isLoading ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <div className="grid gap-4 rounded-lg border border-dashed border-border/70 bg-muted/20 p-6 md:grid-cols-[1fr_auto]">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 text-primary">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-base font-semibold">
                    Analyze who ranks for your next post?
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Pick a keyword from Keywords research, then we tear down the top 10 results.
                  </p>
                </div>
              </div>
              <ul className="grid gap-2 pl-1 text-sm text-muted-foreground sm:grid-cols-2">
                <li>· What Google's rewarding for this search</li>
                <li>· The angle that will set your page apart</li>
                <li>· A ready-to-use outline with 4–8 section headings</li>
                <li>· What competitors missed — and how your pages should score</li>
              </ul>
            </div>
            <div className="flex items-end justify-end">
              {project ? (
                <CompetitorAnalysisCreateButton
                  projects={[project]}
                  defaultProjectId={projectId}
                />
              ) : null}
            </div>
          </div>
        ) : (
          <ul className="space-y-2">
            {rows.map((a) => {
              const done = isSuccess(a.status);
              const running = isInProgress(a.status);
              const outlines = Array.isArray(a.outline_hints)
                ? a.outline_hints.length
                : 0;
              return (
                <li key={a.id}>
                  <Link
                    to="/competitor-analyses/$analysisId"
                    params={{ analysisId: a.id }}
                    className="group grid gap-2 rounded-md border border-border/70 bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent/30 sm:grid-cols-[minmax(0,1fr)_auto]"
                  >
                    <div className="min-w-0 space-y-1.5">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium group-hover:text-primary">
                          {a.keyword}
                        </span>
                        <span className="rounded border border-border/70 px-1.5 py-0 text-[10px] uppercase text-muted-foreground">
                          {a.language}-{a.country}
                        </span>
                      </div>
                      {done ? (
                        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                          <span>{a.competitors_included ?? 0} pages analyzed</span>
                          <span>{a.clusters_found ?? 0} topic groups</span>
                          <span>{a.gaps_found ?? 0} gaps to fill</span>
                          <span>{outlines} outline sections</span>
                        </div>
                      ) : running ? (
                        <div className="text-xs text-muted-foreground">
                          Phase:{' '}
                          <span className="text-foreground">
                            {humanStatus(String(a.progress?.['phase'] ?? a.status))}
                          </span>{' '}
                          · started {relTime(a.started_at ?? a.created_at)}
                        </div>
                      ) : (
                        <div className="text-xs text-muted-foreground">
                          {relTime(a.created_at)}
                          {a.failure_reason ? ` · ${a.failure_reason}` : ''}
                        </div>
                      )}
                    </div>
                    <StatusPill status={a.status} />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      {project ? (
        <div className="rounded-md border border-dashed border-border/60 bg-muted/20 p-3 text-xs text-muted-foreground">
          This studies who already ranks for one keyword, once. To keep watching
          specific rival sites over time,{' '}
          <button
            type="button"
            onClick={() => onSwitchMode('tracking')}
            className="font-medium text-foreground underline underline-offset-2 hover:text-primary"
          >
            switch to “Watch specific competitors”
          </button>
          . To find which keywords to chase, use{' '}
          <Link
            to="/keywords"
            className="font-medium text-foreground underline underline-offset-2 hover:text-primary"
          >
            Keywords
          </Link>
          .
        </div>
      ) : null}
    </>
  );
}
